import type { Product, ProductDashboardItem, ProjectWithTasks } from '~/types';
import { ProjectStatus } from '~/types/api/enums';

interface TimeFilterOption {
  value: TimeFilterValue;
  label: string;
}

/**
 * Values both section dropdowns can take. 'all' applies no time bound.
 * Must stay value-identical to the backend's DashboardTimeWindow Literal
 * (apps/api/src/constants.py) — no codegen link between them today, so
 * check that file when changing this one.
 */
export type TimeFilterValue = 'day' | 'week' | 'month' | 'all';

/** Options for both time-filter dropdowns, in display order. */
export const TIME_FILTER_OPTIONS: TimeFilterOption[] = [
  { value: 'day', label: 'Last 24 hours' },
  { value: 'week', label: 'Last 7 days' },
  { value: 'month', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
];

/**
 * Default window for both sections, applied server-side. Matches the
 * backend's DEFAULT_DASHBOARD_TIME_WINDOW (constants.py).
 */
export const DEFAULT_TIME_FILTER: TimeFilterValue = 'week';

/**
 * How many most-recently-completed projects org-wide the Recently Completed
 * section shows. Mirrors the backend's DASHBOARD_RECENTLY_COMPLETED_LIMIT,
 * which now applies this same cap at the SQL layer (ORDER BY updated_at DESC
 * LIMIT) — the client-side .slice(0, limit) below stays as a defensive cap:
 * the dual-path product/repository grouping can place one completed project
 * under more than one product, so the flattened, cross-product list can
 * still exceed this limit even though each product's own slice does not.
 */
export const RECENTLY_COMPLETED_LIMIT = 10;

/** A product+project pair, the flattened unit both selectors filter/sort/cap and return directly. */
export interface ProductProjectEntry {
  product: Product;
  project: ProjectWithTasks;
}

function byUpdatedAtDescending(a: ProjectWithTasks, b: ProjectWithTasks): number {
  return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
}

function flattenProjects(items: ProductDashboardItem[]): ProductProjectEntry[] {
  return items.flatMap(({ product, projects }) => projects.map(project => ({ product, project })));
}

/**
 * Selects every `IN_PROGRESS` project from the (already server-side,
 * time-window-scoped) dashboard payload, as a flat list ordered by recency
 * (no per-product grouping).
 *
 * Date windowing now happens server-side (the `in_progress_window` query
 * param `useProductDashboard` sends) — this selector only re-derives the
 * status split + sort from whatever the API already returned, so it no
 * longer takes a `now`/`timeFilter` pair.
 *
 * @param items - Raw dashboard items, i.e. `useProductDashboard()`'s `data`.
 * @returns Flat entries ordered by `project.updatedAt` descending.
 */
export function selectInProgressProjects(items: ProductDashboardItem[]): ProductProjectEntry[] {
  return flattenProjects(items)
    .filter(({ project }) => project.status === ProjectStatus.IN_PROGRESS)
    .sort((a, b) => byUpdatedAtDescending(a.project, b.project));
}

/**
 * Selects the `limit` most recently completed (`DONE`) projects from the
 * (already server-side, time-window-scoped and SQL-`LIMIT`-capped) dashboard
 * payload, as a flat list ordered by recency (no per-product grouping).
 *
 * Date windowing now happens server-side (the `completed_window` query param
 * `useProductDashboard` sends) — this selector only re-derives the status
 * split + sort + defensive cap from whatever the API already returned, so it
 * no longer takes a `now` param.
 *
 * @param items - Raw dashboard items, i.e. `useProductDashboard()`'s `data`.
 * @param limit - Global cap on completed projects returned (default `RECENTLY_COMPLETED_LIMIT`).
 * @returns Flat entries ordered by `project.updatedAt` descending, capped at `limit`.
 */
export function selectRecentlyCompleted(
  items: ProductDashboardItem[],
  limit: number = RECENTLY_COMPLETED_LIMIT,
): ProductProjectEntry[] {
  return flattenProjects(items)
    .filter(({ project }) => project.status === ProjectStatus.DONE)
    .sort((a, b) => byUpdatedAtDescending(a.project, b.project))
    .slice(0, limit);
}
